import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { logAccess } from '@/lib/accessLogs';
import { resolveAnalysesToCatalogueIds } from '@/lib/analyseMatcher';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import AnalyseChecklist, { CATEGORIES_ANALYSES } from '@/components/feature/AnalyseChecklist';
import PrintableBonLaboratoire from '@/components/feature/PrintableBonLaboratoire';

interface Patient {
  id: string;
  nom: string;
  prenom: string;
}

interface SageFemmeInfo {
  id: string;
  nom: string;
  prenom: string;
}

export default function SageFemmeBonLaboratoirePage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [sageFemmeInfo, setSageFemmeInfo] = useState<SageFemmeInfo | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [searchPatientTerm, setSearchPatientTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bonCree, setBonCree] = useState(false);
  const [bonId, setBonId] = useState<string>('');
  const [isPrinting, setIsPrinting] = useState(false);

  const [selectedAnalyses, setSelectedAnalyses] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [urgences, setUrgences] = useState(false);
  const [observationsCliniques, setObservationsCliniques] = useState('');
  const [dbCustomAnalyses, setDbCustomAnalyses] = useState<{ nom: string; items: { id: string; label: string }[] }[]>([]);
  const [catalogueEntries, setCatalogueEntries] = useState<{ id: string; code: string; nom: string }[]>([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'sage_femme' && role !== 'super_admin') { navigate('/', { replace: true }); return; }
    loadData();
  }, [user, authLoading, role]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: sfData } = await supabase
        .from('sage_femmes')
        .select('id, nom, prenom')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (sfData) setSageFemmeInfo(sfData);

      const { data: patientsData } = await supabase
        .from('patients').select('id, nom, prenom').order('nom', { ascending: true });
      if (patientsData) setPatients(patientsData);

      // Charger les analyses personnalisées depuis la base
      loadCustomAnalysesFromDb();

      // Charger le catalogue complet pour le matching
      loadCatalogueForMatching();
    } catch (err) { console.error('Erreur chargement:', err); }
    finally { setLoading(false); }
  };

  const loadCustomAnalysesFromDb = async () => {
    try {
      const { data } = await supabase
        .from('analyses_biochimie_catalogue')
        .select('nom')
        .eq('categorie', 'Analyses personnalisées')
        .order('nom');
      if (data && data.length > 0) {
        const items = data.map((a: any) => ({
          id: a.nom,
          label: a.nom,
        }));
        setDbCustomAnalyses([{ nom: 'Analyses personnalisées', items }]);
      }
    } catch (err) {
      console.error('Erreur chargement analyses personnalisées:', err);
    }
  };

  const loadCatalogueForMatching = async () => {
    try {
      const { data } = await supabase
        .from('analyses_biochimie_catalogue')
        .select('id, code, nom');
      if (data) setCatalogueEntries(data);
    } catch (err) { console.error('Erreur chargement catalogue:', err); }
  };

  const filteredPatients = patients.filter(p => {
    const term = searchPatientTerm.toLowerCase();
    if (!term) return true;
    return p.nom.toLowerCase().includes(term) || p.prenom.toLowerCase().includes(term) ||
      `${p.prenom} ${p.nom}`.toLowerCase().includes(term);
  });

  const toggleAnalyse = (id: string) => {
    setSelectedAnalyses(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const getAnalysesLabels = () => {
    const allItems = CATEGORIES_ANALYSES.flatMap(c => c.items);
    return selectedAnalyses.map(id => {
      const item = allItems.find(i => i.id === id);
      return item ? item.label : id;
    });
  };

  const handlePrintImageReady = () => {
    setTimeout(() => {
      window.print();
      setTimeout(() => setIsPrinting(false), 500);
    }, 300);
  };

  const handlePrint = () => {
    if (selectedAnalyses.length === 0) {
      alert('Veuillez sélectionner au moins une analyse avant d\'imprimer');
      return;
    }
    if (!selectedPatientId) {
      alert('Veuillez sélectionner une patiente avant d\'imprimer');
      return;
    }
    const img = new Image();
    img.src = 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/letterhead.webp';
    img.onload = () => setIsPrinting(true);
    img.onerror = () => setIsPrinting(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedAnalyses.length === 0) {
      alert('Veuillez sélectionner au moins une analyse');
      return;
    }

    if (!selectedPatientId || !sageFemmeInfo) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const analysesLabels = getAnalysesLabels();
    const typeAnalyseStr = analysesLabels.join(' ; ');
    let matchedIds: string[] = [];
    try {
      matchedIds = (await resolveAnalysesToCatalogueIds(analysesLabels, catalogueEntries)).ids;
    } catch (err) {
      console.error('Erreur résolution catalogue:', err);
    }

    setSaving(true);
    try {
      const { data: anyMedecin } = await supabase
        .from('medecins').select('id').limit(1).maybeSingle();

      if (!anyMedecin) throw new Error('Aucun médecin trouvé dans le système');

      const { data: bonData, error } = await supabase
        .from('bons_laboratoire')
        .insert({
          patient_id: selectedPatientId,
          medecin_id: anyMedecin.id,
          sage_femme_id: sageFemmeInfo.id,
          prescripteur_type: 'sage_femme',
          type_analyse: typeAnalyseStr,
          description: description.trim() || null,
          urgences,
          observations_cliniques: observationsCliniques.trim() || null,
          statut: 'en_attente',
          date_prescription: new Date().toISOString().split('T')[0],
          analyses_ids: matchedIds.length > 0 ? matchedIds : null,
        })
        .select().single();

      if (error) throw error;

      if (bonData) {
        setBonId(bonData.id);
      }

      const selectedPatient = patients.find(p => p.id === selectedPatientId);
      if (user && bonData) {
        await logAccess({
          userId: user.id, userRole: 'sage_femme', actionType: 'CREATE',
          resourceType: 'BON_LABORATOIRE', resourceId: bonData.id,
          patientId: selectedPatientId,
          details: {
            patient_nom: selectedPatient ? `${selectedPatient.prenom} ${selectedPatient.nom}` : 'Inconnu',
            type_analyse: typeAnalyseStr, urgences,
            action: 'Création d\'un bon de laboratoire par sage-femme'
          }
        });
      }

      try {
        const { data: laborantins } = await supabase
          .from('laborantins')
          .select('email, nom, prenom, user_id')
          .eq('bloque', false);

        if (laborantins && laborantins.length > 0) {
          const patientName = selectedPatient ? `${selectedPatient.prenom} ${selectedPatient.nom}` : 'Patiente';
          const urgenceLabel = urgences ? 'URGENT - ' : '';

          for (const labo of laborantins) {
            if (labo.email) {
              await supabase.functions.invoke('send-email', {
                body: {
                  to: labo.email,
                  toName: `${labo.prenom} ${labo.nom}`,
                  subject: `${urgenceLabel}Nouvelles analyses - ${patientName}`,
                  htmlContent: '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:40px;font-family:Arial,sans-serif;background:#f5f5f5;"><div style="max-width:600px;margin:0 auto;background:white;padding:40px;border-radius:10px;"><h1 style="color:' + (urgences ? '#ef4444' : '#10B981') + ';margin-bottom:20px;">' + (urgences ? 'Analyses urgentes' : 'Nouvelles analyses') + '</h1><p>Bonjour <strong>' + labo.prenom + '</strong>,</p><p>Un nouveau bon de laboratoire a été créé par une sage-femme :</p><div style="background:#f9fafb;border-left:4px solid ' + (urgences ? '#ef4444' : '#10B981') + ';padding:20px;margin:20px 0;border-radius:5px;"><p style="margin:5px 0;"><strong>Patiente :</strong> ' + patientName + '</p><p style="margin:5px 0;"><strong>Nombre d\'analyses :</strong> ' + selectedAnalyses.length + '</p><p style="margin:5px 0;"><strong>Analyses demandées :</strong></p><ul style="margin:10px 0;padding-left:20px;">' + analysesLabels.map(l => '<li>' + l + '</li>').join('') + '</ul>' + (description ? '<p style="margin:5px 0;"><strong>Description :</strong> ' + description + '</p>' : '') + (observationsCliniques ? '<p style="margin:5px 0;"><strong>Observations :</strong> ' + observationsCliniques + '</p>' : '') + '<p style="margin:5px 0;"><strong>Prescrit par :</strong> Sage-femme</p></div>' + (urgences ? '<div style="background:#fef2f2;border:2px solid #ef4444;padding:15px;margin:20px 0;border-radius:8px;text-align:center;"><p style="color:#dc2626;font-weight:bold;margin:0;">ANALYSE URGENTE - À traiter en priorité</p></div>' : '') + '<p style="color:#666;font-size:14px;">Consultez votre tableau de bord pour prendre en charge cette analyse.</p><hr style="margin:30px 0;border:none;border-top:1px solid #eee;"><p style="color:#999;font-size:12px;text-align:center;">Clinique Sainte-Famille - Service Laboratoire</p></div></body></html>'
                }
              });
            }
          }
          console.log('Notifications envoyées aux laborantins');
        }
      } catch (notifError) {
        console.error('Erreur envoi notifications laborantins:', notifError);
      }

      setBonCree(true);
    } catch (error: any) {
      console.error('Erreur création bon:', error);
      alert('Erreur lors de la création du bon : ' + (error.message || ''));
    } finally { setSaving(false); }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-pink-600 animate-spin"></i>
      </div>
    );
  }

  const selectedPatient = patients.find(p => p.id === selectedPatientId);
  const patientName = selectedPatient ? `${selectedPatient.prenom} ${selectedPatient.nom}` : '';
  const practitionerFullName = sageFemmeInfo
    ? `Sage-femme ${sageFemmeInfo.prenom} ${sageFemmeInfo.nom}`
    : 'Sage-femme';
  const todayStr = new Date().toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="no-print">
        <Header />
      </div>
      <div className="bg-white border-b border-gray-200 no-print">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Bon de Laboratoire</h1>
              <p className="mt-2 text-gray-600">Émettez un bon d'analyse pour le laboratoire</p>
            </div>
            <button onClick={() => navigate('/sage-femme/dashboard')} className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i>Retour
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 no-print">
        {bonCree ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-pink-600"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Bon de laboratoire créé avec succès !</h2>
              <p className="text-gray-600">
                Le bon a été transmis au laboratoire{urgences ? ' en priorité' : ''}.
              </p>
              {selectedAnalyses.length > 0 && (
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {selectedAnalyses.map(id => {
                    const allItems = CATEGORIES_ANALYSES.flatMap(c => c.items);
                    const item = allItems.find(i => i.id === id);
                    return (
                      <span key={id} className="px-3 py-1 bg-pink-50 text-pink-700 text-sm rounded-full border border-pink-200">
                        {item ? item.label : id}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handlePrint}
                className="w-full sm:w-auto px-8 py-3 bg-white text-pink-600 border-2 border-pink-600 rounded-lg hover:bg-pink-50 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-base font-medium"
              >
                <i className="ri-printer-line"></i>
                Imprimer le bon
              </button>
              <button
                onClick={() => navigate('/sage-femme/dashboard')}
                className="w-full sm:w-auto px-8 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-arrow-left-line"></i>
                Retour au tableau de bord
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
            {/* Sélection du patient */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Patiente <span className="text-red-500">*</span>
              </label>
              <div className="relative mb-2">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input type="text" value={searchPatientTerm} onChange={(e) => setSearchPatientTerm(e.target.value)}
                  placeholder="Rechercher une patiente..." className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 outline-none" />
              </div>
              <select value={selectedPatientId} onChange={(e) => setSelectedPatientId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm" required>
                <option value="">Sélectionnez une patiente...</option>
                {filteredPatients.map(p => (
                  <option key={p.id} value={p.id}>{p.prenom} {p.nom}</option>
                ))}
              </select>
            </div>

            {/* Checklist des analyses */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Analyses demandées <span className="text-red-500">*</span>
              </label>
              <AnalyseChecklist
                selectedIds={selectedAnalyses}
                onToggle={toggleAnalyse}
                accentColor="pink"
                extraCategories={dbCustomAnalyses}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description détaillée <span className="text-gray-500">(facultatif)</span></label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                placeholder="Précisions sur les analyses demandées..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none text-sm" maxLength={500} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Observations cliniques <span className="text-gray-500">(facultatif)</span></label>
              <textarea value={observationsCliniques} onChange={(e) => setObservationsCliniques(e.target.value)} rows={4}
                placeholder="Signes cliniques, antécédents, traitement en cours..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none text-sm" maxLength={500} />
            </div>

            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <input type="checkbox" id="urgences" checked={urgences} onChange={(e) => setUrgences(e.target.checked)}
                className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500 cursor-pointer" />
              <label htmlFor="urgences" className="flex items-center gap-2 text-sm font-medium text-amber-800 cursor-pointer">
                <i className="ri-alert-line text-lg"></i>Analyse urgente (à traiter en priorité)
              </label>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
              <button type="button" onClick={() => navigate('/sage-femme/dashboard')}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer">Annuler</button>
              <button
                type="button"
                onClick={handlePrint}
                disabled={selectedAnalyses.length === 0 || !selectedPatientId}
                className="px-6 py-2 bg-white text-pink-600 border-2 border-pink-600 rounded-lg hover:bg-pink-50 transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-printer-line"></i>
                Imprimer
              </button>
              <button type="submit" disabled={saving || selectedAnalyses.length === 0}
                className="px-6 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer">
                {saving ? 'Création en cours...' : `Créer le bon (${selectedAnalyses.length} analyse${selectedAnalyses.length > 1 ? 's' : ''})`}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Print container */}
      {isPrinting && selectedPatient && (
        <div className="print-container">
          <PrintableBonLaboratoire
            patientName={patientName}
            practitionerName={practitionerFullName}
            date={todayStr}
            analyses={getAnalysesLabels()}
            description={description || undefined}
            observations={observationsCliniques || undefined}
            urgences={urgences}
            soins={[]}
            constantes={{ taille: '', poids: '', tension_arterielle: '', temperature: '', libelle: '' }}
            patientAge=""
            patientTelephone=""
            patientProfession=""
            patientAssure={false}
            medecinTraitant=""
            verificationUrl={bonId ? `${window.location.origin}/verifier-document?type=bon_laboratoire&id=${bonId}` : undefined}
            onReady={handlePrintImageReady}
          />
        </div>
      )}

      <div className="no-print">
        <Footer />
      </div>
    </div>
  );
}